/**
 * /admin/agents/pricing — the model catalog (#429 Wave 3 Track F, frontend
 * half of #517). Lives under the Agents sub-nav because the per-agent model
 * dropdown (Agents → Models) is sourced from this same catalog.
 *
 * A table of every row in `modelPricing`, with a filter across the top,
 * because 600 rows are otherwise unscannable.
 *
 * **Nothing here sets a price.** Rates are read-only, and #2337 removed the
 * edit that used to change them along with the flag it set. The reason is
 * worth keeping: a hand-typed price is a price nothing re-checks. Four rows
 * were typed in one afternoon in May and went three months without anything
 * able to look at them again, because the pin they carried told every refresh
 * to skip them.
 *
 * Two writes survive, and neither types a number:
 *
 *   - **Approve / Reject** on a rate change the daily check held back for
 *     review. Approve takes the proposed number; Reject keeps today's. Reject
 *     is temporary by design and the row says so, because nothing pins
 *     anything any more and the next check may propose the same change again.
 *   - **Vision**, an operator's correction of a capability the catalog has
 *     wrong. It saves on the click through the capability route, which cannot
 *     touch a rate, and the row keeps refreshing from upstream afterwards.
 *
 * Provenance badges per row show `_source` and `_unpriced`. `Last checked`
 * shows when the daily check last reached the row, whether or not the price
 * moved — a rate that has held steady for ten weeks is current, and the
 * date beside it should say so rather than reading as abandoned.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui'
import {
  adminPricingAPI,
  type ModelPricingRow,
  type PricingCoverage,
  type PricingCoverageRow,
} from '@/lib/api'
import { parseNamePattern } from '@/lib/agentModelSpec'
import {
  CONTEXT_CHOICES,
  FIELD_LABELS,
  VALUE_CHOICES,
  describeFilter,
  isExcludedOriginCountry,
  matchesFilters,
  type CatalogFilter,
  type FilterFieldId,
} from './catalogFilters'
import {
  CapabilityBadge,
  reasoningBadge,
  formatContextSize,
} from '@/lib/modelCapabilityBadges'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { formatDate } from '@/lib/utils'
import { AgentsSubNav } from '../AgentsSubNav'
import { CostCategoryPill, costCategoryRank, COST_CATEGORY_ORDER } from '@/components/CostCategoryPill'
import { CostBandToggles } from './CostBandToggles'
import { OriginToggle } from './OriginToggle'

/** Country codes spelled for an operator. Mirrors the server's labels by
 * hand, like the other wire literals on this page (#2015). */
const COUNTRY_LABEL: Readonly<Record<string, string>> = {
  US: 'United States', CN: 'China', FR: 'France', CA: 'Canada', IL: 'Israel',
  KR: 'South Korea', GB: 'United Kingdom', JP: 'Japan', AE: 'United Arab Emirates',
  RU: 'Russia', CU: 'Cuba', IR: 'Iran', KP: 'North Korea', VE: 'Venezuela',
  unknown: 'Unknown',
}

/** The band ids, as the API row carries them. Derived from the row type rather
 * than restated so a new band cannot exist on the wire and be missing here. */
type CostCategory = NonNullable<ModelPricingRow['costCategory']>

/**
 * Put a cost condition's values in cheapest→priciest order, so its chip reads
 * "Cost is Low or Medium" however the operator built it (#2011).
 *
 * The filter builder appends each value as its checkbox is ticked, so picking
 * Medium before Low produces `['medium','low']` and a chip that reads
 * backwards. Bands are the one fixed-list field with a meaningful order, which
 * is why this normalizes cost and leaves the yes/no/unknown fields alone —
 * reordering those would change chips this slice has no business touching
 * (PR #2013 Copilot).
 */
function canonicalize(f: CatalogFilter): CatalogFilter {
  if (f.field !== 'cost') return f
  return { ...f, values: COST_CATEGORY_ORDER.filter(b => f.values.includes(b)) }
}

/** Sortable columns on the rate-card table. Restricted to identity +
 * recency — operators rarely sort by individual rate fields, and an
 * unsorted-rates affordance keeps the header row scannable. */
/** #1932 — the rate columns joined the sortable set.
 *
 * They were deliberately left out originally, on the reasoning that operators
 * rarely sort by an individual rate and an unsorted-rates header stays
 * scannable. That premise did not hold: "which models are cheapest to run" is a
 * question operators actually ask, and the only answer on this screen was the
 * coarse `Cost` band pill. A busier header row is the accepted trade. */
type SortKey =
  | 'namePattern'
  | 'provider'
  | 'lastSeen'
  | 'costCategory'
  | 'inputCentsPer1M'
  | 'outputCentsPer1M'
  | 'cacheReadCentsPer1M'
  | 'cacheWriteCentsPer1M'
  | 'listedAt'

/** The rate columns, as a set, so the comparator can ask "is this a rate sort?"
 * without four comparisons that could fall out of step with `FIELDS`. */
const RATE_SORT_KEYS: ReadonlySet<SortKey> = new Set<SortKey>([
  'inputCentsPer1M',
  'outputCentsPer1M',
  'cacheReadCentsPer1M',
  'cacheWriteCentsPer1M',
])

/** Columns in the rate-card table, so the empty-state row spans all of them.
 *
 * A named constant because the previous hard-coded `11` only renders when a
 * filter empties the table — the one path nobody exercises while adding
 * columns, which is exactly how it goes stale unnoticed. Counted from the
 * header: Model, Provider, Country of origin, Cost, four rates, Vision,
 * three capabilities, Added, Source, Last checked, Last used, Review. */
const COLUMN_COUNT = 17

/** Provider to DISPLAY and sort on — never to store.
 *
 * The stored `provider` is a lookup key, not a label: `null` means "match any
 * provider", and `(namePattern, provider)` is the unique key that
 * `lookupPricingFromRows` matches against, trying an exact provider row before
 * falling back to the null-provider one. So the column showed `(any)` on every
 * row — 648 of 648 in prod — while the provider was sitting in plain sight as
 * the `namePattern` prefix.
 *
 * `row.provider ?? prefix` rather than the prefix alone: a row that genuinely
 * carries a provider must show its own, not have it recomputed. Uses the shared
 * `parseNamePattern`, which splits on the FIRST colon — model ids can contain
 * colons, and a naive split would truncate them — and returns `undefined` for a
 * pattern with no colon, which sorts last rather than grouping under ''. */
function displayProvider(row: Pick<ModelPricingRow, 'provider' | 'namePattern'>): string | null {
  return row.provider ?? parseNamePattern(row.namePattern).provider ?? null
}

type DraftField = 'inputCentsPer1M' | 'outputCentsPer1M' | 'cacheReadCentsPer1M' | 'cacheWriteCentsPer1M'
const FIELDS: readonly DraftField[] = [
  'inputCentsPer1M',
  'outputCentsPer1M',
  'cacheReadCentsPer1M',
  'cacheWriteCentsPer1M',
]

/** Fields in the order the picker offers them — capability first, because
 * "can it do the job" is the question that actually narrows 663 rows, then
 * price, then provenance. */
const FILTER_FIELDS: readonly FilterFieldId[] = [
  'toolCalling', 'reasoning', 'vision',
  'cost', 'provider',
  'context', 'added',
  'inputCentsPer1M', 'outputCentsPer1M', 'cacheReadCentsPer1M', 'cacheWriteCentsPer1M',
  'source', 'status',
]

/** One id for the Status condition, shared by the builder and the "In use only"
 * checkbox. Two ids would let the same field carry two conditions that AND
 * together — see `setInUseOnly`. */
const STATUS_FILTER_ID = 'f:status'

/** The one id a cost condition ever carries — the same one `FilterBuilder`
 * mints (`f:${field}`), so the band toggles and the dropdown write the SAME
 * condition rather than two rival ones (#2011). Same reasoning as
 * `STATUS_FILTER_ID` above. */
const COST_FILTER_ID = 'f:cost'

/** The one id an origin condition carries — again matching what
 * `FilterBuilder` mints, so the toggle and the dropdown write the same
 * condition rather than two rival ones (#2015). */
const ORIGIN_FILTER_ID = 'f:origin'

const RATE_FIELDS: readonly FilterFieldId[] = [
  'inputCentsPer1M', 'outputCentsPer1M', 'cacheReadCentsPer1M', 'cacheWriteCentsPer1M',
]

/**
 * Build one condition at a time (#1987).
 *
 * Pick a field, then a value, then Add. The shape of the value control follows
 * the field: a checklist for the fields with fixed choices, a number and a
 * direction for the rates, a magnitude for context, a date for Added.
 *
 * There is no operator to choose between conditions — they always AND. Where a
 * field needs alternatives, the checklist supplies them and the chip reads "is
 * Low or Medium", so the joining rule stays one word.
 */
function FilterBuilder({
  providers,
  onAdd,
}: {
  providers: readonly string[]
  onAdd: (filter: CatalogFilter) => void
}) {
  const [field, setField] = useState<FilterFieldId | ''>('')
  const [picked, setPicked] = useState<readonly string[]>([])
  const [op, setOp] = useState<'atLeast' | 'atMost'>('atMost')
  const [amount, setAmount] = useState('')
  const [dateOp, setDateOp] = useState<'after' | 'before'>('after')
  const [date, setDate] = useState('')

  function reset(next: FilterFieldId | ''): void {
    setField(next)
    setPicked([])
    setAmount('')
    setDate('')
  }

  const choices = field === 'provider'
    ? providers.map(p => ({ value: p, label: p }))
    : field ? VALUE_CHOICES[field] : undefined

  const isRate = RATE_FIELDS.includes(field as FilterFieldId)
  const canAdd = field !== ''
    && (choices ? picked.length > 0
      : field === 'added' ? date !== ''
        : amount !== '' && Number.isFinite(Number(amount)))

  function add(): void {
    // Narrowing lives in this guard rather than in `canAdd`, so the compiler
    // can see that `f` is a real field below.
    const f = field
    if (f === '' || !canAdd) return
    // A stable id per field so adding a second condition on the same column
    // replaces the first — two conditions on one column would silently
    // contradict each other, and changing your mind is the likelier intent.
    if (choices) {
      onAdd({ id: `f:${f}`, field: f as never, values: picked })
    } else if (f === 'added') {
      onAdd({ id: 'f:added', field: 'added', op: dateOp, value: date })
    } else {
      onAdd({ id: `f:${f}`, field: f as never, op, value: Number(amount) })
    }
    reset('')
  }

  return (
    <div className="rounded-md border border-dashed p-3 space-y-3" data-testid="filter-builder">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label htmlFor="catalog-filter-field">Add a filter</Label>
          <select
            id="catalog-filter-field"
            aria-label="Filter field"
            className="h-9 rounded-md border border-input bg-transparent px-2 text-sm"
            value={field}
            onChange={(e) => reset(e.target.value as FilterFieldId | '')}
          >
            <option value="">Choose a field…</option>
            {FILTER_FIELDS.map(f => (
              <option key={f} value={f}>{FIELD_LABELS[f]}</option>
            ))}
          </select>
        </div>

        {choices && (
          <div className="flex flex-wrap items-center gap-3 pb-1">
            {choices.map(c => (
              <label key={c.value} className="flex items-center gap-1.5 text-sm">
                <input
                  type="checkbox"
                  aria-label={c.label}
                  checked={picked.includes(c.value)}
                  onChange={(e) =>
                    setPicked(prev =>
                      e.target.checked ? [...prev, c.value] : prev.filter(v => v !== c.value),
                    )
                  }
                />
                {c.label}
              </label>
            ))}
          </div>
        )}

        {field === 'context' && (
          <div className="flex items-end gap-2">
            <select
              aria-label="Context direction"
              className="h-9 rounded-md border border-input bg-transparent px-2 text-sm"
              value={op}
              onChange={(e) => setOp(e.target.value as 'atLeast' | 'atMost')}
            >
              <option value="atLeast">at least</option>
              <option value="atMost">at most</option>
            </select>
            <select
              aria-label="Context size"
              className="h-9 rounded-md border border-input bg-transparent px-2 text-sm"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            >
              <option value="">Choose…</option>
              {CONTEXT_CHOICES.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
        )}

        {isRate && (
          <div className="flex items-end gap-2">
            <select
              aria-label="Rate direction"
              className="h-9 rounded-md border border-input bg-transparent px-2 text-sm"
              value={op}
              onChange={(e) => setOp(e.target.value as 'atLeast' | 'atMost')}
            >
              <option value="atMost">at most</option>
              <option value="atLeast">at least</option>
            </select>
            <Input
              aria-label="Rate value"
              type="number"
              min={0}
              className="w-28"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
        )}

        {field === 'added' && (
          <div className="flex items-end gap-2">
            <select
              aria-label="Date direction"
              className="h-9 rounded-md border border-input bg-transparent px-2 text-sm"
              value={dateOp}
              onChange={(e) => setDateOp(e.target.value as 'after' | 'before')}
            >
              <option value="after">after</option>
              <option value="before">before</option>
            </select>
            <Input
              aria-label="Date"
              type="date"
              className="w-40"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
        )}

        <Button type="button" size="sm" onClick={add} disabled={!canAdd}>
          Add filter
        </Button>
      </div>
    </div>
  )
}

export function PricingPage() {
  const [rows, setRows] = useState<ModelPricingRow[] | null>(null)
  /** #2107 — this deployment keeps excluded models out of the catalog, so
   * there is nothing here to filter. Server-supplied; the page cannot derive
   * it. */
  const [originPolicyActive, setOriginPolicyActive] = useState(false)
  const [coverage, setCoverage] = useState<PricingCoverage | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  /** #1987 — the conditions an operator has added, in the order they added
   * them. The name box and the "In use only" checkbox are NOT separate state:
   * they read and write this list, so a chip and a tickbox can never disagree.
   * The alternative — three independent pieces of state kept in sync — is the
   * shape that produces a checkbox saying one thing and a chip another. */
  const [filters, setFilters] = useState<readonly CatalogFilter[]>([])
  const [sortBy, setSortBy] = useState<SortKey>('namePattern')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

  async function reload(): Promise<void> {
    setLoading(true)
    try {
      // Both queries are independent — fire in parallel.
      const [r, c] = await Promise.all([
        adminPricingAPI.list(),
        adminPricingAPI.coverage(),
      ])
      setRows(r.rows)
      setOriginPolicyActive(r.originPolicyActive === true)
      setCoverage(c)
      setLoadError(null)
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load model pricing')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
  }, [])

  /** modelSpec → coverage row. Powers the in-use filter, the
   * last-used sort, and the last-used column. */
  const coverageByPattern = useMemo(() => {
    const m = new Map<string, PricingCoverageRow>()
    for (const c of coverage?.models ?? []) m.set(c.modelSpec, c)
    return m
  }, [coverage])

  /** Set of in-use modelSpecs — drives the filter toggle. */
  const inUseSet = useMemo(() => new Set(coverageByPattern.keys()), [coverageByPattern])

  /** The name box's value, which IS the "name" chip. Reading it out of the
   * filter list rather than holding it twice is what makes "clearing the chip
   * clears the box" true by construction instead of by an effect. */
  const nameFilter = filters.find(f => f.field === 'name')
  const filter = nameFilter && nameFilter.field === 'name' ? nameFilter.text : ''

  function setFilter(text: string): void {
    setFilters(prev => {
      const rest = prev.filter(f => f.field !== 'name')
      return text ? [...rest, { id: 'name', field: 'name', text }] : rest
    })
  }

  /** "In use only" is a shortcut for the Status condition, not a second
   * mechanism — so it toggles the `inUse` VALUE inside the one Status filter
   * rather than adding a filter of its own.
   *
   * An earlier version gave the checkbox its own filter id. Review caught what
   * that costs: an operator who also picked Status in the builder would end up
   * with two Status conditions AND'd together, quietly over-narrowing, and
   * unticking the box would leave the builder's own "In use" value behind. One
   * id is what actually makes "they cannot disagree" true. */
  const statusFilter = filters.find(f => f.field === 'status')
  const inUseOnly = statusFilter?.field === 'status' && statusFilter.values.includes('inUse')

  function setInUseOnly(on: boolean): void {
    setFilters(prev => {
      const existing = prev.find(f => f.field === 'status')
      const current = existing?.field === 'status' ? existing.values : []
      const next = on
        ? [...current.filter(v => v !== 'inUse'), 'inUse']
        : current.filter(v => v !== 'inUse')
      const rest = prev.filter(f => f.field !== 'status')
      // Dropping the last value drops the condition — an empty Status chip
      // would match nothing and read as a filter the operator never set.
      return next.length > 0 ? [...rest, { id: STATUS_FILTER_ID, field: 'status', values: next }] : rest
    })
  }

  /** The bands currently selected, read out of the one cost condition rather
   * than held separately — the same trick the name box and "In use only" use,
   * and what makes "removing the chip clears the toggles" true by construction
   * instead of by an effect (#2011). */
  const costFilter = filters.find(f => f.field === 'cost')
  const selectedCostBands = (costFilter?.field === 'cost' ? costFilter.values : []) as CostCategory[]

  /** Write the next selection into that one condition. Dropping the last band
   * drops the condition entirely — an empty Cost chip would match nothing and
   * read as a filter the operator never set (mirrors `setInUseOnly`). */
  function setCostBands(next: CostCategory[]): void {
    setFilters(prev => {
      const rest = prev.filter(f => f.field !== 'cost')
      return next.length > 0 ? [...rest, { id: COST_FILTER_ID, field: 'cost', values: next }] : rest
    })
  }

  /** Whether the origin filter is on, read out of the one condition rather
   * than held separately (#2015) — the same trick the cost bands use, which is
   * what makes "removing the chip turns the toggle off" true by construction. */
  const originFilter = filters.find(f => f.field === 'origin')
  const originActive = originFilter?.field === 'origin' && originFilter.values.includes('notExcluded')

  function setOriginFilter(on: boolean): void {
    // #2107 — with the policy on there is no control to write this, and a
    // condition seeded some other way would put a chip in the filter row that
    // matches everything and clears to no visible effect.
    if (originPolicyActive) return
    setFilters(prev => {
      const rest = prev.filter(f => f.field !== 'origin')
      return on ? [...rest, { id: ORIGIN_FILTER_ID, field: 'origin', values: ['notExcluded'] }] : rest
    })
  }

  /** What the predicate needs that a row does not carry. `providerOf` is the
   * same derivation the Provider column and its sort use, and "in use" lives in
   * the coverage response — passing both in keeps one implementation of each,
   * so the filter can never disagree with what the table displays. */
  const filterCtx = useMemo(
    () => ({
      providerOf: displayProvider,
      isInUse: (r: ModelPricingRow) => inUseSet.has(r.namePattern),
    }),
    [inUseSet],
  )

  /** Providers actually present, so the picker never offers one that would
   * return nothing. Derived the same way the column displays it. */
  const providerChoices = useMemo(() => {
    const set = new Set<string>()
    for (const r of rows ?? []) {
      const p = displayProvider(r)
      if (p) set.add(p)
    }
    return [...set].sort()
  }, [rows])

  /** Adding a condition on a field that already has one REPLACES it — two
   * conditions on one column would silently contradict each other, and an
   * operator changing their mind is far more likely than wanting both. */
  function addFilter(next: CatalogFilter): void {
    setFilters(prev => [...prev.filter(f => f.id !== next.id), canonicalize(next)])
  }

  /** One pass over every condition. The name box and the in-use checkbox are
   * conditions in this same list rather than special cases before it, which is
   * what stops them drifting from what the chips say. */
  const filtered = useMemo(
    () => (rows ?? []).filter(r => matchesFilters(r, filters, filterCtx)),
    [rows, filters, filterCtx],
  )

  /**
   * How many models each band would show if it were the selected one — every
   * OTHER active condition applied, the cost condition ignored (#2011).
   *
   * This is the only rule under which the count, the toggle's unavailable
   * state and the row count tell one story. Both alternatives lie:
   *
   *   - A GLOBAL count lets Premium advertise 66 while an active
   *     `Provider is anthropic` leaves none of them, so a toggle that says it
   *     has rows empties the table when clicked.
   *   - Counting the CURRENTLY VISIBLE rows has the mirror fault: selecting
   *     Low drives every other band to zero, and the whole row greys itself
   *     out as though nothing else existed.
   *
   * Ignoring only the cost condition is what makes a count a prediction rather
   * than a description. Because the name box and "In use only" live in the
   * same list, they are included with no special case.
   */
  /**
   * How many rows the origin toggle would hide, with every OTHER active
   * condition applied (#2015). Same rule as the cost-band counts: the number
   * has to predict what clicking does, not describe the whole catalog.
   */
  const originHiddenCount = useMemo(() => {
    const others = filters.filter(f => f.field !== 'origin')
    let n = 0
    for (const r of rows ?? []) {
      if (!isExcludedOriginCountry(r.origin?.country)) continue
      if (matchesFilters(r, others, filterCtx)) n++
    }
    return n
  }, [rows, filters, filterCtx])

  const costBandCounts = useMemo(() => {
    const others = filters.filter(f => f.field !== 'cost')
    const counts: Record<CostCategory, number> = { low: 0, medium: 0, high: 0, premium: 0 }
    for (const r of rows ?? []) {
      // An unpriced model belongs to no band, so it is counted under none —
      // matching the "—" its Cost cell shows.
      if (!r.costCategory) continue
      if (matchesFilters(r, others, filterCtx)) counts[r.costCategory]++
    }
    return counts
  }, [rows, filters, filterCtx])

  /** Apply the active sort. Nulls (missing last-seen for never-used
   * rows) always sort to the bottom regardless of direction —
   * "no data" isn't a meaningful comparison. */
  const visible = useMemo(() => {
    const arr = [...filtered]
    arr.sort((a, b) => {
      // #1362 — rows with a pending swing always float to the top of
      // the visible list so the admin sees them before any sort key
      // applies. The page banner already names the count; this keeps
      // the affected rows visible without scrolling.
      const aPending = !!a.pendingProposedAt
      const bPending = !!b.pendingProposedAt
      if (aPending !== bPending) return aPending ? -1 : 1
      // #1844 Slice 2 — cost category sorts numerically by its rank (Low →
      // Premium), with unpriced last (rank handles that).
      if (sortBy === 'costCategory') {
        const cmp = costCategoryRank(a.costCategory) - costCategoryRank(b.costCategory)
        return sortDir === 'asc' ? cmp : -cmp
      }
      // #1932 — one comparator for all four rate columns.
      //
      // Deliberately NOT shaped like the cost branch above, which reverses a
      // rank for descending. A rate is optional, and "no rate set" must sort
      // last in BOTH directions — so the missing-value check happens before
      // direction is applied, exactly as the string branch below does for
      // nulls. Folding it into the direction flip instead would float unpriced
      // models to the top of a descending sort, where they read as the most
      // expensive rather than the unknown ones.
      // #1985 — "Added" sorts newest/oldest, with never-listed rows last in
      // BOTH directions, the same rule the rate columns follow. Most of the
      // catalog has no date (LiteLLM publishes none), so floating those to the
      // top of a descending sort would bury the column's only real content.
      if (sortBy === 'listedAt') {
        const at = a.listedAt ? Date.parse(a.listedAt) : NaN
        const bt = b.listedAt ? Date.parse(b.listedAt) : NaN
        const aMissing = Number.isNaN(at)
        const bMissing = Number.isNaN(bt)
        if (aMissing && bMissing) return 0
        if (aMissing) return 1
        if (bMissing) return -1
        const cmp = at - bt
        return sortDir === 'asc' ? cmp : -cmp
      }
      if (RATE_SORT_KEYS.has(sortBy)) {
        // `DraftField` is already exactly this union — the four editable rate
        // fields — so a second `RateField` type would be a copy that could
        // drift from `FIELDS`.
        const an = a[sortBy as DraftField]
        const bn = b[sortBy as DraftField]
        const aMissing = typeof an !== 'number'
        const bMissing = typeof bn !== 'number'
        if (aMissing && bMissing) return 0
        if (aMissing) return 1
        if (bMissing) return -1
        const cmp = an - bn
        return sortDir === 'asc' ? cmp : -cmp
      }
      let av: string | null
      let bv: string | null
      if (sortBy === 'lastSeen') {
        av = coverageByPattern.get(a.namePattern)?.lastSeenAt ?? null
        bv = coverageByPattern.get(b.namePattern)?.lastSeenAt ?? null
      } else if (sortBy === 'provider') {
        // The same derivation the cell renders, so what an operator sees
        // grouped is what they see displayed.
        av = displayProvider(a)
        bv = displayProvider(b)
      } else {
        av = a.namePattern
        bv = b.namePattern
      }
      if (av === null && bv === null) return 0
      if (av === null) return 1
      if (bv === null) return -1
      const cmp = av.localeCompare(bv)
      return sortDir === 'asc' ? cmp : -cmp
    })
    return arr
  }, [filtered, sortBy, sortDir, coverageByPattern])

  function toggleSort(key: SortKey): void {
    if (sortBy === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else {
      setSortBy(key)
      setSortDir('asc')
    }
  }

  // #1362 — count rows the daily refresh stashed as "needs review."
  // The page-level banner renders only when this is positive; declared
  // here ABOVE the loading/error early-returns so hook order stays
  // stable across renders (React Hook rule).
  const pendingSwingCount = useMemo(
    () => (rows ?? []).filter(r => r.pendingProposedAt).length,
    [rows],
  )

  if (loading && !rows) {
    return (
      <div className="space-y-6 max-w-full">
        <AgentsSubNav />
        <p className="text-sm text-muted-foreground">Loading model pricing…</p>
      </div>
    )
  }
  if (loadError && !rows) {
    return (
      <div className="space-y-6 max-w-full">
        <AgentsSubNav />
        <p className="text-sm text-destructive" role="alert">
          {loadError}
        </p>
      </div>
    )
  }
  if (!rows) return null

  const unpriced = coverage?.unpricedInUseCount ?? 0

  return (
    <div className="space-y-6 max-w-full">
      <AgentsSubNav />
      <div>
        <h1 className="text-2xl font-semibold">Model catalog</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Every model we know about — what it costs, what it can do, and where that came from.
          A daily check keeps every rate in step with what the model's supplier publishes, so
          nobody types a price here. <strong>Last checked</strong> is when that check last
          looked at a model, whether or not its price moved. Rates are in{' '}
          <em>cents per 1,000,000 tokens</em>.
        </p>
      </div>

      {/* #1362 pending-swing banner. Shown only when the refresh
          stashed at least one suspect rate change. The banner is the
          headline cue; the per-row red treatment + Approve / Reject
          buttons below are where the operator actually acts. */}
      {pendingSwingCount > 0 && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
          <strong className="text-destructive">
            {pendingSwingCount} price change{pendingSwingCount === 1 ? '' : 's'} need{pendingSwingCount === 1 ? 's' : ''} review
          </strong>
          <span className="text-muted-foreground">
            {' '}— the upstream feed proposed rates that changed more than 10× from the current value.
            Approve to apply, or reject to keep the existing rate. Suspect rows are highlighted below.
          </span>
        </div>
      )}

      {/* Coverage banner — the headline gap signal. Red when in-use
          models lack a real rate card (those tokens contribute $0 to
          the cost rollup); quietly green when every in-use model is
          priced. Hidden while coverage is still loading so the banner
          doesn't flash false-clean before the data arrives. */}
      {coverage && (unpriced > 0 ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
          <strong className="text-destructive">
            {unpriced} model{unpriced === 1 ? '' : 's'} in use {unpriced === 1 ? 'has' : 'have'} no pricing
          </strong>
          <span className="text-muted-foreground"> — those tokens contribute $0 to the cost rollup. Use the </span>
          <button
            type="button"
            className="underline font-medium hover:text-foreground"
            onClick={() => setInUseOnly(true)}
          >
            "In use only" filter
          </button>
          <span className="text-muted-foreground"> to find them; rows tagged </span>
          <Badge variant="warning" className="inline-flex">unpriced</Badge>
          <span className="text-muted-foreground"> need rates.</span>
        </div>
      ) : (
        <div className="rounded-md border border-muted bg-muted/40 p-3 text-sm text-muted-foreground">
          ✓ Every in-use model has pricing assigned.
        </div>
      ))}

      <Card>
        <CardHeader>
          <CardTitle>{rows.length} {rows.length === 1 ? 'model' : 'models'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* #2015 — the origin cut, above the cost bands: it is a policy
              decision rather than a preference, and everything below refines
              what it leaves. */}
          <OriginToggle
            active={originActive}
            hiddenCount={originHiddenCount}
            onToggle={setOriginFilter}
            policyActive={originPolicyActive}
          />

          {/* #2011 — the cost bands, one click away. Above the name box because
              it is the coarsest cut an operator makes and the one they reach
              for first; the finer conditions read as a refinement below it. */}
          <CostBandToggles
            selected={selectedCostBands}
            counts={costBandCounts}
            onChange={setCostBands}
          />

          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1 flex-1 min-w-[240px]">
              <Label htmlFor="pricing-filter">Filter by name pattern</Label>
              <Input
                id="pricing-filter"
                type="search"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="e.g. claude-haiku, openai:gpt"
              />
            </div>
            <label className="flex items-center gap-2 text-sm whitespace-nowrap pb-2">
              <input
                type="checkbox"
                checked={inUseOnly}
                onChange={(e) => setInUseOnly(e.target.checked)}
              />
              In use only
              {coverage && (
                <span className="text-xs text-muted-foreground">
                  ({inUseSet.size} of {rows.length})
                </span>
              )}
            </label>
          </div>
          <FilterBuilder providers={providerChoices} onAdd={addFilter} />

          {filters.length > 0 && (
            <div className="flex flex-wrap items-center gap-2" data-testid="filter-chips">
              {filters.map((f, i) => (
                <span key={f.id} className="flex items-center gap-2">
                  {i > 0 && <span className="text-xs text-muted-foreground">and</span>}
                  <span className="inline-flex items-center gap-2 rounded-full border px-3 py-0.5 text-xs">
                    {describeFilter(f)}
                    <button
                      type="button"
                      aria-label={`Remove filter ${describeFilter(f)}`}
                      className="text-muted-foreground hover:text-foreground"
                      onClick={() => setFilters(prev => prev.filter(x => x.id !== f.id))}
                    >
                      ×
                    </button>
                  </span>
                </span>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => setFilters([])}>
                Clear all
              </Button>
            </div>
          )}

          {filters.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Showing {visible.length} of {rows.length} rows
            </p>
          )}

          {/* #1932 — 14 columns no longer fit. Scroll the table, never the
              page: a horizontally-scrolling body would push the filter and
              the coverage banner off-screen. */}
          <div className="border rounded-md overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortHead onClick={() => toggleSort('namePattern')} active={sortBy === 'namePattern'} dir={sortDir}>Model</SortHead>
                  <SortHead onClick={() => toggleSort('provider')} active={sortBy === 'provider'} dir={sortDir}>Provider</SortHead>
                  {/* #2015 — distinct from Provider beside it: that is the
                      serving path, this is who built the model. */}
                  <TableHead className="text-xs">Country of origin</TableHead>
                  <SortHead onClick={() => toggleSort('costCategory')} active={sortBy === 'costCategory'} dir={sortDir}>Cost</SortHead>
                  <SortHead onClick={() => toggleSort('inputCentsPer1M')} active={sortBy === 'inputCentsPer1M'} dir={sortDir} align="right">Input ¢/1M</SortHead>
                  <SortHead onClick={() => toggleSort('outputCentsPer1M')} active={sortBy === 'outputCentsPer1M'} dir={sortDir} align="right">Output ¢/1M</SortHead>
                  <SortHead onClick={() => toggleSort('cacheReadCentsPer1M')} active={sortBy === 'cacheReadCentsPer1M'} dir={sortDir} align="right">Cache read ¢/1M</SortHead>
                  <SortHead onClick={() => toggleSort('cacheWriteCentsPer1M')} active={sortBy === 'cacheWriteCentsPer1M'} dir={sortDir} align="right">Cache write ¢/1M</SortHead>
                  <TableHead className="text-center">Vision enabled</TableHead>
                  {/* #1932 — capability columns, following the `Vision enabled`
                      precedent that this tab is where per-model facts live.
                      Read-only, unlike the vision checkbox: upstream is the
                      source of truth here, and a bare checkbox could not show
                      whether a value came from the catalog or an operator. */}
                  <TableHead className="text-center">Tool calling</TableHead>
                  <TableHead className="text-center">Reasoning</TableHead>
                  <TableHead className="text-right">Context</TableHead>
                  {/* #1985 — "Added", not "Released": the upstream value is
                      when the catalog listed the model, which is a good proxy
                      for a launch date but is not one. */}
                  <SortHead onClick={() => toggleSort('listedAt')} active={sortBy === 'listedAt'} dir={sortDir} align="right">Added</SortHead>
                  <TableHead>Source</TableHead>
                  {/* #2337 — when the daily check last reached this row, which
                      is a different question from `updatedAt`: that one says
                      when the number last moved, and a rate holding steady for
                      ten weeks reads as abandoned under it. */}
                  <TableHead>Last checked</TableHead>
                  <SortHead onClick={() => toggleSort('lastSeen')} active={sortBy === 'lastSeen'} dir={sortDir}>Last used</SortHead>
                  {/* Empty on most rows: the only thing left to act on here is
                      a price change the daily check held back for review. */}
                  <TableHead className="text-right">Review</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((row) => (
                  <PricingRow
                    key={row._id}
                    row={row}
                    lastSeenAt={coverageByPattern.get(row.namePattern)?.lastSeenAt}
                    onSaved={(updated) =>
                      setRows((prev) =>
                        prev ? prev.map((r) => (r._id === updated._id ? updated : r)) : prev,
                      )
                    }
                  />
                ))}
                {visible.length === 0 && (
                  <TableRow>
                    {/* Spans every column, so the message stays centred under
                        the whole table. Only renders when a filter empties the
                        list, which is why a stale value here goes unnoticed. */}
                    <TableCell colSpan={COLUMN_COUNT} className="text-center text-sm text-muted-foreground py-8">
                      {inUseOnly ? 'No in-use rows match the filter.' : 'No rows match the filter.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

/** Sortable column header. Click toggles between this column +
 * ascending; clicking the active column flips the direction. */
function SortHead({
  children,
  onClick,
  active,
  dir,
  align = 'left',
}: {
  children: React.ReactNode
  onClick: () => void
  active: boolean
  dir: 'asc' | 'desc'
  /** #1932 — the rate columns are right-aligned to match their numeric cells.
   * Without this the header text would sit left of the numbers it labels. */
  align?: 'left' | 'right'
}) {
  // `aria-sort` belongs on the `columnheader` (TableHead → <th>),
  // not the interactive button — screen readers read the attribute
  // off the cell. The button remains the focusable affordance.
  return (
    <TableHead
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={align === 'right' ? 'text-right' : undefined}
    >
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1 hover:text-foreground"
      >
        {children}
        {active && <span className="text-[10px]">{dir === 'asc' ? '▲' : '▼'}</span>}
      </button>
    </TableHead>
  )
}

// ── Per-row component ──────────────────────────────────────────────────

function PricingRow({
  row,
  lastSeenAt,
  onSaved,
}: {
  row: ModelPricingRow
  /** ISO timestamp the model last appeared in a finalized run, from
   * the coverage report. Undefined when the row has never been used
   * — rendered as an em-dash. */
  lastSeenAt?: string
  onSaved: (updated: ModelPricingRow) => void
}) {
  const tz = useUserTimezone()
  const [savingVision, setSavingVision] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)
  const [actingOnSwing, setActingOnSwing] = useState<'approve' | 'reject' | null>(null)

  const hasPendingSwing = !!row.pendingProposedAt
  const busy = savingVision || actingOnSwing !== null

  /** Clear the transient "Saved." note when a DIFFERENT model lands in this
   * slot — sorting and filtering reuse row components.
   *
   * Keyed on `_id` alone, not on `updatedAt`. A successful save moves
   * `updatedAt`, so keying on it would clear the note on the very write that
   * produced it and nobody would ever see the confirmation.
   *
   * The ref is what makes this correct rather than an optimization (#1924):
   * React flushes passive effects after the commit, and the app root renders
   * inside `React.StrictMode`, which double-invokes them. Initialising from
   * the row makes mount a no-op, which is safe only because `error` and
   * `savedAt` already start at the values this would set. */
  const seededId = useRef(row._id)
  useEffect(() => {
    if (seededId.current === row._id) return
    seededId.current = row._id
    setError(null)
    setSavedAt(null)
  }, [row._id])

  /** Write the vision correction straight through, with no draft in between.
   *
   * It is one boolean and there is nothing left on the row to batch it with,
   * so a Save button would be a second click for no gain. The route it calls
   * cannot touch a rate (#2337). */
  async function handleVisionChange(next: boolean) {
    setSavingVision(true)
    setError(null)
    try {
      const updated = await adminPricingAPI.setCapabilityOverride(row._id, 'imageInput', next)
      setSavedAt(Date.now())
      onSaved(updated)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSavingVision(false)
    }
  }

  async function handleApproveSwing() {
    setActingOnSwing('approve')
    setError(null)
    try {
      const updated = await adminPricingAPI.approveSwing(row._id)
      setSavedAt(Date.now())
      onSaved(updated)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Approve failed')
    } finally {
      setActingOnSwing(null)
    }
  }

  async function handleRejectSwing() {
    setActingOnSwing('reject')
    setError(null)
    try {
      const updated = await adminPricingAPI.rejectSwing(row._id)
      setSavedAt(Date.now())
      onSaved(updated)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reject failed')
    } finally {
      setActingOnSwing(null)
    }
  }

  return (
    <TableRow
      // Tinted background + left border for any row that the daily
      // refresh stashed as a swing under review (#1362). Same red
      // family as the page-level banner so the operator can scan the
      // table and find them.
      className={hasPendingSwing ? 'bg-destructive/5 border-l-4 border-l-destructive' : undefined}
    >
      <TableCell className="font-mono text-xs align-top">
        <div>{row.namePattern}</div>
        {error && (
          <div className="text-destructive text-xs mt-1" role="alert">
            {error}
          </div>
        )}
        {savedAt && !error && (
          <div className="text-success text-xs mt-1" key={savedAt}>
            Saved.
          </div>
        )}
      </TableCell>
      <TableCell className="text-xs align-top">
        {displayProvider(row) ?? <span className="text-muted-foreground">—</span>}
      </TableCell>
      <TableCell className="text-xs align-top">
        {/* One muted `unknown` when we cannot place the model, per the
            mockup — showing a maker slug beside "Unknown" would fill the
            column with noise in the one case it has nothing to say. */}
        {row.origin?.makerLabel && row.origin.country !== 'unknown'
          ? (
            <span>
              {row.origin.makerLabel}
              <span className="text-muted-foreground">
                {' · '}{COUNTRY_LABEL[row.origin.country] ?? row.origin.country}
              </span>
            </span>
          )
          : <span className="text-muted-foreground">unknown</span>}
      </TableCell>
      {/* #1844 Slice 2 — relative cost category (rank band). The raw Input/
          Output cells below are the editable rate card, the only place a
          dollar figure appears. */}
      <TableCell className="align-top">
        <CostCategoryPill category={row.costCategory} />
      </TableCell>
      {FIELDS.map((f) => {
        // The pending field on the row mirrors each rate field —
        // `inputCentsPer1M` → `pendingInputCentsPer1M`, etc. When set,
        // surface the proposed value beneath the input so the admin
        // sees current vs proposed at a glance. (#1362)
        const pendingKey =
          `pending${f.charAt(0).toUpperCase()}${f.slice(1)}` as
          | 'pendingInputCentsPer1M'
          | 'pendingOutputCentsPer1M'
          | 'pendingCacheReadCentsPer1M'
          | 'pendingCacheWriteCentsPer1M'
        const proposed = row[pendingKey]
        return (
          <TableCell key={f} className="text-right align-top">
            {/* Read-only, permanently (#2337). Nobody types a price here any
                more: a number somebody typed is a number nothing re-checks,
                and the four rows that were typed in went three months
                without anything able to look at them again.
                An em dash for an unset rate, never `0` — a model with no
                rate is not a free model, and the sort agrees. */}
            <span className="text-xs tabular-nums pt-2 inline-block">
              {typeof row[f] === 'number'
                ? row[f]
                : <span className="text-muted-foreground">—</span>}
            </span>
            {/* The swing display is deliberately unchanged, and shows on a row
                that is not in edit mode: an operator must see a proposed value
                without first asking to edit. */}
            {proposed !== undefined && (
              <div className="text-xs text-destructive mt-1">
                proposed: {proposed}
              </div>
            )}
          </TableCell>
        )
      })}
      {/* The one correction an operator can still make here (#2337). It
          saves on the click: there is nothing left on the row to batch it
          with, so a Save button would be a second click for no gain. The
          route it calls cannot touch a rate, and the row keeps refreshing
          from upstream afterwards. */}
      <TableCell className="text-center align-top">
        <div className="flex justify-center pt-2">
          <Checkbox
            checked={row.imageInput === true}
            onCheckedChange={(v) => void handleVisionChange(v === true)}
            disabled={busy}
            aria-label={`vision enabled for ${row.namePattern}`}
          />
        </div>
      </TableCell>
      {/* #1932 — read-only capability cells. `CapabilityBadge` renders three
          states and shows NOTHING for unknown, so a model the catalog is silent
          about never reads as one that cannot. */}
      <TableCell className="text-center align-top">
        <div className="flex justify-center pt-2">
          <CapabilityBadge value={row.toolCalling} label="🔧 tool calling" />
        </div>
      </TableCell>
      <TableCell className="text-center align-top">
        <div className="flex justify-center pt-2">{reasoningBadge(row)}</div>
      </TableCell>
      <TableCell className="text-right text-xs align-top">
        <div className="pt-2">{formatContextSize(row.maxInputTokens) ?? '—'}</div>
      </TableCell>
      <TableCell className="text-xs text-muted-foreground text-right align-top">
        {/* Same em-dash-for-absent treatment as Last used beside it. Most rows
            are LiteLLM and will never carry a date. */}
        <div className="pt-2">{row.listedAt ? formatDate(row.listedAt, tz) : '—'}</div>
      </TableCell>
      <TableCell className="align-top">
        <div className="flex flex-col gap-1">
          {row._source && (
            <Badge variant={badgeVariantForSource(row._source)} className="w-fit">
              {row._source}
            </Badge>
          )}
          {row._unpriced && (
            <Badge variant="warning" className="w-fit">
              unpriced
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell className="text-xs text-muted-foreground align-top">
        {/* When a refresh last looked, not when the number last moved
            (#2337). A rate that has held steady for ten weeks is current,
            and the date beside it should say so. An em dash means no
            refresh has reached this row yet, which is true of a
            hand-discovered placeholder until the next daily pass. */}
        {row.lastCheckedAt ? formatDate(row.lastCheckedAt, tz) : '—'}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground align-top">
        {lastSeenAt ? formatDate(lastSeenAt, tz) : '—'}
      </TableCell>
      <TableCell className="text-right align-top">
        {/* Two actions, and only on a row the refresh held back for review.
            Approve writes the proposed rates to the live fields; Reject
            leaves the live rates alone. Neither types a number, which is
            why both survived the removal of the rate edit (#2337).

            Reject is temporary by design, and the note beside it says so:
            nothing pins a row any more, so the next refresh is free to
            propose the same change again. */}
        {hasPendingSwing && (
          <div className="flex flex-col gap-1 items-end">
            <div className="flex gap-1">
              <Button
                type="button"
                size="sm"
                variant="default"
                onClick={handleApproveSwing}
                disabled={busy}
              >
                {actingOnSwing === 'approve' ? 'Approving…' : 'Approve'}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleRejectSwing}
                disabled={busy}
              >
                {actingOnSwing === 'reject' ? 'Rejecting…' : 'Reject'}
              </Button>
            </div>
            <div className="text-xs text-muted-foreground max-w-52 text-right">
              Turning this down keeps today's price. The next daily check may
              suggest it again.
            </div>
          </div>
        )}
      </TableCell>
    </TableRow>
  )
}

/** Pick a badge variant for the row's `_source` value. The three
 * provenance values map to different visual treatments so an operator
 * can scan a 200-row table and spot the unusual rows fast. */
/** #1928 — `'openrouter'` was missing from this parameter type while the server
 * has been writing it all along (the majority of rows). Correcting
 * `ModelPricingRow._source` in `api.ts` made the compiler surface it here,
 * which is the point: the type was lying and TypeScript could not see it
 * because the value arrives as JSON.
 *
 * Widened only, and #1932 settled that it stays that way: OpenRouter shares
 * LiteLLM's `secondary` styling on purpose. The badge renders `_source` as its
 * label, so the two have always been distinguishable by the word they show —
 * only the color is shared, and spending one of three variants to restate
 * what the label already says would leave nothing for a source that matters
 * more. */
function badgeVariantForSource(
  source: 'litellm' | 'openrouter' | 'ottocloud' | 'override' | 'discovery',
): 'default' | 'secondary' | 'warning' {
  switch (source) {
    // Kept for a row on a database that has not booted #2337 yet. Nothing
    // writes this source any more and the boot migration moves the last
    // rows off it, but the badge renders whatever the row says, and an
    // unstyled badge on a row an admin is looking at is worse than a
    // styling for a value that is on its way out.
    case 'override':
      return 'default'
    case 'discovery':
      return 'warning'
    // The account's own prices (#2429). Shares the neutral variant with
    // the other catalogs, because the label already says which one it is
    // and the three variants are worth more on states than on sources.
    case 'ottocloud':
      return 'secondary'
    case 'litellm':
    default:
      return 'secondary'
  }
}

export default PricingPage
